import type { PulseChart } from '@shared/chart';
import { findActiveBurstChamber } from '@shared/chart';
import type { EngineState } from '@shared/engine/pulse/state';
import {
  BURST_CHAMBER_COLOUR,
  BURST_CHAMBER_COLOUR_SOFT,
  PLAYFIELD_WIDTH,
} from '@shared/gameTypes';

/**
 * Stateless drawers for burst-chamber visuals: the upcoming/active bands
 * laid along the lane and the live "MASH" overlay rendered on top of
 * everything else while the player is inside a chamber. Pulled out so the
 * main renderer doesn't have to host two 50+ LOC chamber-specific methods.
 */

export function drawBurstChamberBands(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  state: EngineState,
  beat: number,
  playerX: number,
  ceilingY: number,
  floorY: number,
  xPerBeat: number,
  viewWidth: number,
): void {
  const chambers = chart.burstChambers;
  if (!chambers || chambers.length === 0) {
    return;
  }
  for (const burst of chambers) {
    if (burst.endBeat < beat - 1) {
      continue;
    }
    if (burst.startBeat > beat + viewWidth / xPerBeat + 1) {
      continue;
    }
    // Time-earlier edge is the visual LEFT edge (pads scroll right→left).
    const x0 = playerX + (burst.startBeat - beat) * xPerBeat;
    const x1 = playerX + (burst.endBeat - beat) * xPerBeat;
    const isActive = beat >= burst.startBeat && beat < burst.endBeat;
    const isDone = state.completedBurstChambers.has(burst.id);
    const pulse = isActive ? 0.75 + Math.sin(beat * Math.PI * 4) * 0.25 : 0.5;

    ctx.save();
    ctx.globalAlpha = isDone ? 0.06 : isActive ? 0.18 : 0.1;
    ctx.fillStyle = BURST_CHAMBER_COLOUR;
    ctx.fillRect(x0, ceilingY, x1 - x0, floorY - ceilingY);

    ctx.globalAlpha = isDone ? 0.2 : 0.55 + (isActive ? pulse * 0.3 : 0);
    ctx.fillStyle = BURST_CHAMBER_COLOUR;
    ctx.shadowColor = BURST_CHAMBER_COLOUR;
    ctx.shadowBlur = isActive ? 18 + pulse * 16 : 10;
    const stripeH = 3;
    ctx.fillRect(x0, ceilingY - stripeH - 1, x1 - x0, stripeH);
    ctx.fillRect(x0, floorY + 1, x1 - x0, stripeH);
    ctx.restore();

    if (!isActive && !isDone && burst.startBeat - beat < 4) {
      // Anchor the warning just inside the time-START edge of the chamber
      // (visually the LEFT edge under right→left scroll) so it's visible
      // as the player approaches even if the chamber extends off the
      // canvas to the right. Clamp to playerX+80 so the label doesn't drift
      // behind the player once the chamber crosses the receptor; falls
      // back to inside the time-END edge for narrow chambers.
      const labelX = Math.max(playerX + 80, Math.min(x0 + 60, x1 - 60));
      const labelY = (ceilingY + floorY) / 2;
      ctx.save();
      ctx.globalAlpha = Math.min(1, 1 - (burst.startBeat - beat) / 4);
      ctx.font = 'bold 18px "Rajdhani", "Orbitron", system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = BURST_CHAMBER_COLOUR;
      ctx.shadowBlur = 14;
      ctx.fillStyle = BURST_CHAMBER_COLOUR;
      ctx.fillText('GET READY', labelX, labelY - 8);
      ctx.font = 'bold 14px "Rajdhani", "Orbitron", system-ui, sans-serif';
      ctx.fillStyle = BURST_CHAMBER_COLOUR_SOFT;
      ctx.fillText('MASH!', labelX, labelY + 14);
      ctx.restore();
    }
  }
}

/**
 * Big foreground panel rendered on top of everything when the playhead
 * is inside an active burst chamber. Shows the live tap counter and a
 * progress bar for time remaining; this is the primary "tap as fast as
 * you can RIGHT NOW" affordance.
 */
export function drawActiveBurstOverlay(
  ctx: CanvasRenderingContext2D,
  chart: PulseChart,
  state: EngineState,
  beat: number,
  w: number,
  ceilingY: number,
  floorY: number,
): void {
  const active = findActiveBurstChamber(chart.burstChambers, beat);
  if (!active) {
    return;
  }
  const tapCount = state.burstTaps.get(active.id) ?? 0;
  const total = active.endBeat - active.startBeat;
  const elapsed = beat - active.startBeat;
  const progress = Math.max(0, Math.min(1, elapsed / total));
  // Ramp the pulse with elapsed time so the urgency builds toward the
  // end of the chamber.
  const pulsePhase = (beat * Math.PI * 4) % (Math.PI * 2);
  const pulse = 0.5 + Math.sin(pulsePhase) * 0.5;

  ctx.save();
  const bandH = floorY - ceilingY;
  const cy = (ceilingY + floorY) / 2;
  ctx.globalAlpha = 0.12 + pulse * 0.08;
  ctx.fillStyle = BURST_CHAMBER_COLOUR;
  ctx.fillRect(0, ceilingY, w, bandH);

  // Scale the headline + counter with viewport width so a phone-sized
  // 320px canvas doesn't get a 64px MASH! that overflows.
  const sizeScale = Math.max(0.5, Math.min(1, w / PLAYFIELD_WIDTH));
  const headlineSize = (64 + pulse * 18) * sizeScale;
  const counterSize = 30 * sizeScale;
  const headlineGap = 18 * sizeScale;
  const counterGap = 32 * sizeScale;

  ctx.globalAlpha = 0.95;
  ctx.font = `900 ${headlineSize}px "Rajdhani", "Orbitron", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = BURST_CHAMBER_COLOUR;
  ctx.shadowBlur = 24 + pulse * 18;
  ctx.fillStyle = '#fff';
  ctx.fillText('MASH!', w / 2, cy - headlineGap);

  ctx.shadowBlur = 12;
  ctx.font = `bold ${counterSize}px "Rajdhani", "Orbitron", system-ui, sans-serif`;
  ctx.fillStyle = BURST_CHAMBER_COLOUR_SOFT;
  ctx.fillText(`${tapCount} taps`, w / 2, cy + counterGap);

  const barW = Math.min(360, w * 0.5);
  const barH = 6;
  const barX = (w - barW) / 2;
  const barY = cy + 60 * sizeScale;
  ctx.shadowBlur = 0;
  ctx.globalAlpha = 0.4;
  ctx.fillStyle = '#fff';
  ctx.fillRect(barX, barY, barW, barH);
  ctx.globalAlpha = 1;
  ctx.fillStyle = BURST_CHAMBER_COLOUR;
  ctx.shadowColor = BURST_CHAMBER_COLOUR;
  ctx.shadowBlur = 12;
  ctx.fillRect(barX, barY, barW * (1 - progress), barH);
  ctx.restore();
}
